import React, {useEffect, useState, useRef} from 'react'
import {injectIntl} from 'react-intl'
import styled from 'styled-components'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import {Delete} from '@mui/icons-material'
import Autocomplete from 'components/Autocomplete'
import Editor from 'components/Editor'
import {getTableEmailOptions} from 'utils/utils'

const Row = styled.div`
    display: flex;
    margin-bottom: 20px;

    :first-child {
        margin-top: 20px;
    }
`

const Panel = styled.div`
    height: 0;
    overflow: hidden;
    transition: height 300ms ease-out;
`

const Title = styled.div`
    padding: 5px 10px;
    font-size: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    background: #415dd4;
    color: #fff;

    > button {
        margin-left: auto;
        padding: 5px;
        color: #fff;
    }
`

const Container = styled.div`
    margin-bottom: 10px;

    :last-child {
        margin-bottom: 30px;
    }
`

const Field = styled(TextField)`
	width: calc(50% - 15px);
	margin-right: 30px;

	&:nth-child(2n) {
		margin-right: 0px;
	}
`

const Notification = ({template, index, emissions, substitutions, forms,
    fields, handleUpdate, handleDelete, dates, numbers, intl}) => {
        
    const [open, toggleOpen] = useState(true)
    const [tables, getTables] = useState([])
    const expanded = useRef(null)

    useEffect(() => {
        const data = getTableEmailOptions(template.emissionPath)
        getTables(data)
    }, [template.emissionPath])

	useEffect(() => {
		const el = expanded.current
		el.style.height = el.scrollHeight + "px"
    }, [template.body])

    const handleClick = () => {
        const el = expanded.current

        if (open) {
            el.style.height = 0
        } else {
            el.style.height = el.scrollHeight + "px"
        }
        
        toggleOpen(!open)
    }

    const handleChange = (e) => {
        if (e.target.name === 'emissionPath') {
            const value = emissions.find(item => item.id === e.target.value).pathId
            handleUpdate(e.target.name, value, index)
        } else if (e.target.name === 'cc' || e.target.name === 'bcc') {
            const value = e.target.value ? e.target.value.split(',').map(item => item.trim()) : []
            handleUpdate(e.target.name, value, index)
        } else {
            handleUpdate(e.target.name, e.target.value, index)
        }
    }

    const handleUpdateTemplate = (value) => {
        handleUpdate('body', value, index)
    }

    const handleDeleteTemplate = (e) => {
        e.stopPropagation()
        handleDelete(index)
    }

    return (
        <Container>
            <Title onClick={handleClick}>
                {'Email ' + template.subject}
                <IconButton onClick={handleDeleteTemplate}>
                    <Delete />
                </IconButton>
            </Title>
            <Panel ref={expanded}>
                <Row>
                    <FormControl sx={{width: 'calc(50% - 15px)', marginRight: '30px',}} variant="standard">
                        <InputLabel>
                            {intl.formatMessage({id: "sidebar.sent_to"})}
                        </InputLabel>
                        <Select
                            value={template.emissionPath.join(',')}
                            onChange={handleChange}
                            inputProps={{
                                name: 'emissionPath'
                            }}
                        >
                            {emissions.map(item =>
                                <MenuItem key={item.id} value={item.id}>
                                    {item.pathName}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Autocomplete
                        style={{height: '100%', width: 'calc(50% - 15px)'}}
                        value={template.subject}
                        label={intl.formatMessage({id: "sidebar.subject"})}
                        onChange={(value) => handleUpdate('subject', value, index)}
                        options={substitutions.map(field => field.name)}
                        component='input'
                    />
                </Row>
                <Row>
                    <Field variant="standard"
                        defaultValue={template.fromEmail}
                        onChange={handleChange}
                        label={intl.formatMessage({id: "sidebar.from_email"})}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            maxLength: global.NAME_MAX_LENGTH,
                            name: 'fromEmail'
                        }}
                    />
                    <Field variant="standard"
                        defaultValue={template.fromName}
                        onChange={handleChange}
                        label={intl.formatMessage({id: "sidebar.from_name"})}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            maxLength: global.NAME_MAX_LENGTH,
                            name: 'fromName'
                        }}
                    />
                </Row>
                <Row>
                    <Field variant="standard"
                        defaultValue={template.cc.join(', ')}
                        onChange={handleChange}
                        label={intl.formatMessage({id: "sidebar.carbon_copy"})}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            name: 'cc'
                        }}
                    />
                    <Field variant="standard"
                        defaultValue={template.bcc.join(', ')}
                        onChange={handleChange}
                        label={intl.formatMessage({id: "sidebar.blind_carbon_copy"})}
                        InputLabelProps={{
                            shrink: true
                        }}
                        inputProps={{
                            name: 'bcc'
                        }}
                    />
                </Row>
                <Editor
                    subs={substitutions}
                    forms={forms}
                    tables={tables}
                    fields={fields}
                    value={template.body}
                    onChange={handleUpdateTemplate}
                    urls={substitutions.filter(item => item.type === 'url')}
                    dates={dates}
                    numbers={numbers}
            />
            </Panel>
        </Container>
    )
}

export default injectIntl(Notification)
